// ============================================================
// NetworkingEdgeDevices.jsx
//
// Course content page for "Networking & Edge Devices" lesson.
// Rewritten to:
//   1) remove three dead imports (FaCloud, FaShieldAlt, FaTerminal
//      — none were ever rendered),
//   2) complete the edge-node code example — the original used
//      `client` and `THRESHOLD` without ever declaring them and
//      had no setup()/Wi-Fi/MQTT connection, so it wouldn't compile,
//   3) rename "Deterministic Speed" to "Lower Latency" — edge
//      processing reduces round-trip time, but "deterministic"
//      implies a guaranteed worst-case timing bound that this
//      general claim doesn't actually establish,
//   4) match the app-wide dark-purple/goldenrod look,
//   5) use arrow-function components and Material Icons only.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import MemoryIcon from "@mui/icons-material/Memory";
import RouterIcon from "@mui/icons-material/Router";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import BoltIcon from "@mui/icons-material/Bolt";
import DnsIcon from "@mui/icons-material/Dns";
import HubIcon from "@mui/icons-material/Hub";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import PsychologyIcon from "@mui/icons-material/Psychology";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const ARCHITECTURE = [
    { title: "Edge Node", icon: <MemoryIcon />, color: BRAND.gold, description: "The localized 'brain' (like an ESP32 or Raspberry Pi) that filters and analyzes raw sensor data before it ever leaves the room." },
    { title: "IoT Gateway", icon: <RouterIcon />, color: BRAND.teal, description: "The bridge between local protocols (Zigbee/Bluetooth) and global networks (Wi-Fi/cellular/cloud)." },
    { title: "Edge Computing", icon: <AnalyticsIcon />, color: BRAND.gold, description: "Reducing round-trip time by performing AI/ML computation physically close to the data source." },
];

const EDGE_ADVANTAGES = [
    { title: "Lower Latency", desc: "Decisions happen in milliseconds, not seconds — useful for autonomous vehicles and robotics." },
    { title: "Enhanced Privacy", desc: "Keep sensitive data on-premise. Only send anonymized summaries to the cloud." },
    { title: "Bandwidth Efficiency", desc: "Don't send 24/7 video streams — only send a notification when an event is detected." },
];

const EDGE_HARDWARE = [
    { name: "Raspberry Pi 5", role: "Local automation server", icon: <DeveloperBoardIcon /> },
    { name: "ESP32-S3", role: "AI vision/audio node", icon: <MemoryIcon /> },
    { name: "NVIDIA Jetson", role: "High-performance AI edge", icon: <PsychologyIcon /> },
    { name: "Industrial PLC", role: "Legacy machine interface", icon: <PrecisionManufacturingIcon /> },
];

// Sub-components (arrow-function style, defined before first use)

const FeatureCard = ({ title, icon, color, description }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: color, transform: "translateY(-4px)" },
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: `${color}26`, color }}>{icon}</Avatar>
            <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>{description}</Typography>
    </Paper>
);

const CodeTerminal = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Typography component="pre" sx={{ p: 2.5, color: "#FCD34D", fontSize: 13, fontFamily: "'Fira Code', monospace", overflowX: "auto" }}>
                {code}
            </Typography>
        </Box>
    );
};

// A complete, compiling sketch — the original referenced `client` and
// `THRESHOLD` without declaring either, and had no setup() at all.
const EDGE_NODE_SKETCH = `#include <WiFi.h>
#include <PubSubClient.h>

WiFiClient espClient;
PubSubClient client(espClient);

const int SENSOR_PIN = 34;
const int THRESHOLD = 800; // tune to your sensor's expected range

void setup() {
  WiFi.begin("SSID", "PASSWORD");
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
  }
  client.setServer("mqtt.eclipseprojects.io", 1883);
  client.connect("Edge_Node_01");
}

void loop() {
  int rawData = analogRead(SENSOR_PIN);

  if (rawData > THRESHOLD) {
    // Decision made AT THE EDGE — only the cloud alert crosses the network
    client.publish("alerts/critical", "Threshold exceeded!");
  }
  client.loop();
}`;

// ── Main Page ───────────────────────────────────────────────────

const NetworkingEdgeDevices = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Module 06: Infrastructure"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    Networking & <span style={{ color: BRAND.teal }}>Edge Devices</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    Intelligence at the source. Learn how edge devices process data locally to cut latency,
                    save bandwidth, and enable real-time autonomous decisions.
                </Typography>
            </Box>

            {/* ARCHITECTURE OVERVIEW */}
            <Box sx={{ mb: 10 }}>
                <Grid container spacing={3}>
                    {ARCHITECTURE.map((item) => (
                        <Grid item xs={12} md={4} key={item.title}>
                            <FeatureCard {...item} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* THE EDGE ADVANTAGE */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <BoltIcon sx={{ color: BRAND.gold }} /> Why Edge Over Cloud?
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={2}>
                            {EDGE_ADVANTAGES.map((item) => (
                                <Box key={item.title} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${BRAND.border}`, bgcolor: "rgba(255,255,255,0.03)" }}>
                                    <Typography variant="subtitle2" sx={{ color: BRAND.gold }}>{item.title}</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>{item.desc}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CodeTerminal label="EDGE_NODE_INIT.ino" code={EDGE_NODE_SKETCH} />
                    </Grid>
                </Grid>
            </Box>

            {/* DEVICE HIERARCHY */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4 }}>Hardware for the Edge</Typography>
                <Grid container spacing={3}>
                    {EDGE_HARDWARE.map((dev) => (
                        <Grid item xs={6} md={3} key={dev.name}>
                            <Paper sx={{ p: 3, textAlign: "center", bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                <Box sx={{ fontSize: 32, mb: 1, color: BRAND.gold, display: "flex", justifyContent: "center" }}>{dev.icon}</Box>
                                <Typography variant="subtitle2" sx={{ color: BRAND.white }}>{dev.name}</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>{dev.role}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <SettingsInputAntennaIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to Bridge the Gap?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Design your network architecture and deploy your first edge gateway.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Launch Gateway Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="Architecture Patterns" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default NetworkingEdgeDevices;
